// Native smart-segment types for the Loyalty module. These mirror the
// `stores/{storeId}/loyalty/segments` backend contract one-to-one (spec §3.1 of
// the 2026-08-21 tracking-module design, extended by the 2026-08 builder-page
// contract) — the wire is snake_case exactly as typed here, so the gateway
// passes payloads through untouched.

// ── Criteria ─────────────────────────────────────────────────────────────────

export type SegmentRuleField =
  // Customer Properties
  | 'order_count'
  | 'lifetime_value'
  | 'aov'
  | 'total_spend'
  | 'joined_days'
  | 'tier'
  // Purchase Behavior
  | 'last_order_days'
  | 'product_category'
  | 'avg_items_per_order'
  | 'promo_orders_pct'
  // Browsing & Engagement
  | 'last_seen_days'
  | 'total_site_visits'
  | 'avg_session_mins'
  | 'pages_per_visit'
  | 'cart_abandon_rate'
  | 'email_engagement'
  // Events
  | 'event_count'
  // Legacy (still evaluated; hidden from the builder's picker)
  | 'first_order_days'
  | 'birth_month'
  | 'birthday_this_month'
  | 'email_subscribed'
  | 'sms_subscribed'
  | 'promo_order_count'
  | 'pickup_pct'
  | 'points_balance'
  | 'lifetime_points'
  | 'points_redeemed'
  | 'pts_to_next_tier'
  | 'rewards_redeemed'
  | 'last_redemption_days'
  | 'referrals_count'
  | 'reviews_written'

export type SegmentRuleOp =
  | 'gte'
  | 'lte'
  | 'gt'
  | 'lt'
  | 'eq'
  | 'neq'
  | 'between'
  | 'in'
  | 'nin'

export type SegmentMatch = 'all' | 'any'

export interface SegmentRule {
  field: SegmentRuleField
  op: SegmentRuleOp
  // Numeric ops (gte/lte/eq/neq/between) and bool fields (eq, value 1|0).
  value?: number
  // Upper bound for `between` (inclusive: value..value2).
  value2?: number
  // Non-empty array for in/nin — tier slugs or birth-month ints (1–12).
  values?: (string | number)[]
  // Only allowed on WINDOW_FIELDS — day-based fields carry the window in
  // their value already.
  window_days?: number
  // Event rows are sent as `field: 'event_count'` + `event: <alias>` (one of
  // SEGMENT_EVENT_DEFS keys); the server resolves the alias and stores the
  // rule with `event` kept next to the resolved `metric` (or, for the
  // coupon / points aliases, the rewritten `field`). Sending that back is
  // accepted as-is.
  event?: string
  // Resolved customer_events metric on stored event_count rules. Legacy rules
  // (authored before `event`) carry only this — never set it on new rules.
  metric?: string
  // Optional on event_count (product name / item names / search term
  // contains) and promo_order_count (coupon code contains) — the builder's
  // "containing …" keyword filter. Case-insensitive substring, ≤ 100 chars.
  keyword?: string
}

export interface SegmentCriteria {
  match: SegmentMatch
  rules: SegmentRule[]
}

// Server-enforced cap on criteria.rules length.
export const MAX_SEGMENT_RULES = 10

// ── Field metadata (builder UI) ──────────────────────────────────────────────
//
// Groups, labels, order, operators and the has-done / has-not-done event model
// follow the "Segment Conditions" sheet (utils/data/Segment Conditions.xlsx)
// and the segment-builder mockup (`fields` / `eventTypes`). A sheet row the
// server cannot evaluate yet would be listed as `comingSoon` (picker shows it
// disabled, server rejects it) — none today: the browsing rows are derived
// from storefront sessions. Fields from the earlier loyalty-centric catalog
// are `legacy`: hidden from the picker, but a stored rule using one still
// renders and edits normally.

export type SegmentFieldType = 'number' | 'select' | 'bool'

export type SegmentFieldGroup =
  | 'Customer Properties'
  | 'Purchase Behavior'
  | 'Browsing & Engagement'
  // Legacy-only group (never offered in the picker).
  | 'Loyalty'

// Picker groups, in sheet order.
export const SEGMENT_FIELD_GROUPS: SegmentFieldGroup[] = [
  'Customer Properties',
  'Purchase Behavior',
  'Browsing & Engagement',
]

// Sheet rows without a backend key (none at the moment). Never valid on a rule.
export type SegmentComingSoonField = never

export interface SegmentFieldOption {
  value: string | number
  label: string
}

export interface SegmentFieldDef {
  field: SegmentRuleField | SegmentComingSoonField
  label: string
  group: SegmentFieldGroup
  type: SegmentFieldType
  // Field accepts an optional window_days ("in the last… days") — mirrors the
  // server's WINDOW_FIELDS.
  window: boolean
  // Options for select-type fields (in/nin `values` chips). product_category
  // has none here — the builder fills it from the store's category list.
  options?: SegmentFieldOption[]
  // Short engineering note shown as the row's title tooltip.
  hint?: string
  // Hidden from the "Add condition" picker; existing rules still render/edit.
  legacy?: boolean
  // Shown disabled ("Coming soon") in the picker; no backend support yet.
  comingSoon?: boolean
}

export const SEGMENT_TIER_OPTIONS: SegmentFieldOption[] = [
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'platinum', label: 'Platinum' },
  { value: 'none', label: 'No tier' },
]

export const SEGMENT_BIRTH_MONTH_OPTIONS: SegmentFieldOption[] = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

export const SEGMENT_EMAIL_ENGAGEMENT_OPTIONS: SegmentFieldOption[] = [
  { value: 'engaged', label: 'Engaged' },
  { value: 'unengaged', label: 'Unengaged' },
  { value: 'never_emailed', label: 'Never Emailed' },
]

export const SEGMENT_FIELD_DEFS: SegmentFieldDef[] = [
  // Customer Properties
  {
    field: 'order_count',
    label: 'Total Orders',
    group: 'Customer Properties',
    type: 'number',
    window: true,
    hint: 'Count of all completed orders (online + POS)',
  },
  {
    // The customer page's Lifetime Value figure. All-time only (no window).
    field: 'lifetime_value',
    label: 'Lifetime Value ($)',
    group: 'Customer Properties',
    type: 'number',
    window: false,
    hint: 'Sum of all order totals (the customer page Lifetime Value figure)',
  },
  {
    field: 'aov',
    label: 'Avg Order Value ($)',
    group: 'Customer Properties',
    type: 'number',
    window: true,
    hint: 'Lifetime value ÷ total orders',
  },
  {
    field: 'total_spend',
    label: 'Total Spend ($)',
    group: 'Customer Properties',
    type: 'number',
    window: true,
    hint: 'Same as lifetime value; may diverge if returns are tracked separately',
  },
  {
    field: 'joined_days',
    label: 'Customer Since (days)',
    group: 'Customer Properties',
    type: 'number',
    window: false,
    hint: 'Days since account creation',
  },
  {
    field: 'tier',
    label: 'Loyalty Tier',
    group: 'Customer Properties',
    type: 'select',
    window: false,
    options: SEGMENT_TIER_OPTIONS,
    hint: 'Current loyalty program tier',
  },
  // Purchase Behavior
  {
    field: 'last_order_days',
    label: 'Days Since Last Order',
    group: 'Purchase Behavior',
    type: 'number',
    window: false,
    hint: 'Days elapsed since most recent completed order',
  },
  {
    field: 'product_category',
    label: 'Purchased Category',
    group: 'Purchase Behavior',
    type: 'select',
    window: true,
    hint: 'Product category from any past online order',
  },
  {
    field: 'avg_items_per_order',
    label: 'Avg Items Per Order',
    group: 'Purchase Behavior',
    type: 'number',
    window: true,
    hint: 'Average line-item count across online orders',
  },
  {
    field: 'promo_orders_pct',
    label: 'Promo Usage (% of orders)',
    group: 'Purchase Behavior',
    type: 'number',
    window: true,
    hint: 'Percentage of orders that used a promo code',
  },
  // Browsing & Engagement
  {
    field: 'last_seen_days',
    label: 'Last Seen (days ago)',
    group: 'Browsing & Engagement',
    type: 'number',
    window: false,
    hint: 'Days since last storefront activity (page/product view, cart, search, login)',
  },
  {
    field: 'total_site_visits',
    label: 'Total Site Visits',
    group: 'Browsing & Engagement',
    type: 'number',
    window: true,
    hint: 'Distinct storefront sessions (visits) by the customer',
  },
  {
    field: 'avg_session_mins',
    label: 'Avg Session Duration (min)',
    group: 'Browsing & Engagement',
    type: 'number',
    window: true,
    hint: "Mean minutes from a session's first to last event",
  },
  {
    field: 'pages_per_visit',
    label: 'Pages Per Visit',
    group: 'Browsing & Engagement',
    type: 'number',
    window: true,
    hint: 'Page views per session',
  },
  {
    field: 'cart_abandon_rate',
    label: 'Cart Abandonment Rate (%)',
    group: 'Browsing & Engagement',
    type: 'number',
    window: true,
    hint: '% of checkouts started with no order placed in the next 4 hours',
  },
  {
    field: 'email_engagement',
    label: 'Email Engagement',
    group: 'Browsing & Engagement',
    type: 'select',
    window: false,
    options: SEGMENT_EMAIL_ENGAGEMENT_OPTIONS,
    hint: 'Engagement level based on marketing email open/click activity',
  },
  // ── Legacy (earlier loyalty-centric catalog) ──
  {
    field: 'first_order_days',
    label: 'First Order (days ago)',
    group: 'Customer Properties',
    type: 'number',
    window: false,
    hint: 'Days since first order',
    legacy: true,
  },
  {
    field: 'birth_month',
    label: 'Birth Month',
    group: 'Customer Properties',
    type: 'select',
    window: false,
    options: SEGMENT_BIRTH_MONTH_OPTIONS,
    legacy: true,
  },
  {
    field: 'birthday_this_month',
    label: 'Birthday is this month',
    group: 'Customer Properties',
    type: 'bool',
    window: false,
    legacy: true,
  },
  {
    field: 'email_subscribed',
    label: 'Email subscribed',
    group: 'Customer Properties',
    type: 'bool',
    window: false,
    legacy: true,
  },
  {
    field: 'sms_subscribed',
    label: 'SMS subscribed',
    group: 'Customer Properties',
    type: 'bool',
    window: false,
    legacy: true,
  },
  {
    field: 'promo_order_count',
    label: 'Orders with a promo code',
    group: 'Purchase Behavior',
    type: 'number',
    window: true,
    legacy: true,
  },
  {
    field: 'pickup_pct',
    label: '% of orders that are pickup',
    group: 'Purchase Behavior',
    type: 'number',
    window: true,
    legacy: true,
  },
  {
    field: 'points_balance',
    label: 'Points balance',
    group: 'Loyalty',
    type: 'number',
    window: false,
    legacy: true,
  },
  {
    field: 'lifetime_points',
    label: 'Lifetime points earned',
    group: 'Loyalty',
    type: 'number',
    window: false,
    legacy: true,
  },
  {
    field: 'points_redeemed',
    label: 'Lifetime points redeemed',
    group: 'Loyalty',
    type: 'number',
    window: false,
    legacy: true,
  },
  {
    field: 'pts_to_next_tier',
    label: 'Points to next tier',
    group: 'Loyalty',
    type: 'number',
    window: false,
    legacy: true,
  },
  {
    field: 'rewards_redeemed',
    label: 'Rewards redeemed (count)',
    group: 'Loyalty',
    type: 'number',
    window: true,
    legacy: true,
  },
  {
    field: 'last_redemption_days',
    label: 'Days since last redemption',
    group: 'Loyalty',
    type: 'number',
    window: false,
    legacy: true,
  },
  {
    field: 'referrals_count',
    label: 'Successful referrals',
    group: 'Loyalty',
    type: 'number',
    window: false,
    legacy: true,
  },
  {
    // Matched to the customer by reviewer email; approved + awaiting-moderation.
    field: 'reviews_written',
    label: 'Reviews written',
    group: 'Browsing & Engagement',
    type: 'number',
    window: false,
    legacy: true,
  },
]

export const segmentFieldDef = (field: string): SegmentFieldDef | undefined =>
  SEGMENT_FIELD_DEFS.find((def) => def.field === field)

// Backend-backed defs only (no coming-soon rows).
const isBackedFieldDef = (
  def: SegmentFieldDef,
): def is SegmentFieldDef & { field: SegmentRuleField } => !def.comingSoon

// Fields that accept an optional window_days — mirrors the server's
// WINDOW_FIELDS.
export const SEGMENT_WINDOW_FIELDS: SegmentRuleField[] =
  SEGMENT_FIELD_DEFS.filter(isBackedFieldDef)
    .filter((def) => def.window)
    .map((def) => def.field)

// ── Events ("Has done / Has not done") ──────────────────────────────────────
//
// The sheet's event conditions, keyed by the alias the server resolves
// (`{ field: 'event_count', event: <key> }` on the wire — see SegmentRule).
// `metric` / `field` are the server's resolution and exist only to READ
// legacy stored rules that predate `event` (matched on metric); new rules
// never send them. `keyword` says whether the "containing …" filter has
// something to match against for that event (the sheet's "Supports Keyword
// Filter" column, minus Completed Purchase: Placed Order events carry no
// product names).

export interface SegmentEventDef {
  key: string
  label: string
  // Field the server resolves the alias onto (stored rules come back with it).
  field: 'event_count' | 'promo_order_count' | 'rewards_redeemed'
  // customer_events metric (event_count only). abandoned_cart is derived
  // server-side ("Started Checkout" sessions with no order) — its metric here
  // is the one the pre-alias builder stored for that row.
  metric?: string
  keyword: boolean
  keywordPlaceholder?: string
}

export const SEGMENT_EVENT_DEFS: SegmentEventDef[] = [
  {
    key: 'completed_purchase',
    label: 'Completed Purchase',
    field: 'event_count',
    metric: 'Placed Order',
    keyword: false,
  },
  {
    key: 'visited_website',
    label: 'Visited Website',
    field: 'event_count',
    metric: 'Viewed Page',
    keyword: false,
  },
  {
    key: 'added_to_cart',
    label: 'Added to Cart',
    field: 'event_count',
    metric: 'Added to Cart',
    keyword: true,
    keywordPlaceholder: 'containing product…',
  },
  {
    key: 'abandoned_cart',
    label: 'Abandoned Cart',
    field: 'event_count',
    metric: 'Started Checkout',
    keyword: true,
    keywordPlaceholder: 'containing product…',
  },
  {
    key: 'viewed_product',
    label: 'Viewed Product',
    field: 'event_count',
    metric: 'Viewed Product',
    keyword: true,
    keywordPlaceholder: 'containing product…',
  },
  {
    key: 'searched_site',
    label: 'Searched Site',
    field: 'event_count',
    metric: 'Searched Products',
    keyword: true,
    keywordPlaceholder: 'containing keyword…',
  },
  {
    key: 'clicked_email',
    label: 'Clicked Email',
    field: 'event_count',
    metric: 'Clicked Email',
    keyword: false,
  },
  {
    key: 'opened_email',
    label: 'Opened Email',
    field: 'event_count',
    metric: 'Opened Email',
    keyword: false,
  },
  {
    key: 'redeemed_coupon',
    label: 'Redeemed Coupon',
    field: 'promo_order_count',
    keyword: true,
    keywordPlaceholder: 'containing coupon…',
  },
  {
    key: 'redeemed_points',
    label: 'Redeemed Points',
    field: 'rewards_redeemed',
    keyword: false,
  },
]

export const segmentEventDef = (key: string): SegmentEventDef | undefined =>
  SEGMENT_EVENT_DEFS.find((def) => def.key === key)

// The event definition a stored rule was built from, if any: the `event`
// alias when the server kept it, else (legacy rules) event_count rules match
// on metric. Numeric rules on promo_order_count / rewards_redeemed that were
// NOT authored as events (no `event`, plain count) stay numeric rows.
export const segmentEventDefForRule = (
  rule: SegmentRule,
): SegmentEventDef | undefined =>
  (rule.event ? segmentEventDef(rule.event) : undefined) ??
  (rule.field === 'event_count'
    ? SEGMENT_EVENT_DEFS.find((def) => def.metric === rule.metric)
    : undefined)

// Operator labels. Numeric ops in the sheet's order; `between` compares
// value..value2 inclusive.
export const SEGMENT_NUMERIC_OPS: { value: SegmentRuleOp; label: string }[] = [
  { value: 'eq', label: 'is equal to' },
  { value: 'gt', label: 'is greater than' },
  { value: 'lt', label: 'is less than' },
  { value: 'between', label: 'is between' },
  { value: 'gte', label: 'is at least' },
  { value: 'lte', label: 'is at most' },
]

// Still evaluated by the server but no longer offered — a stored rule using
// one keeps it (the builder lists it only while it's the row's current op).
export const SEGMENT_LEGACY_NUMERIC_OPS: {
  value: SegmentRuleOp
  label: string
}[] = [{ value: 'neq', label: 'is not equal to' }]

// Enum operators: "is" / "is not" are single-choice, "is any of" multi — all
// three store as in/nin with a `values` array.
export interface SegmentSelectOpDef {
  value: SegmentRuleOp
  label: string
  multi: boolean
}

export const SEGMENT_SELECT_OPS: SegmentSelectOpDef[] = [
  { value: 'in', label: 'is', multi: false },
  { value: 'nin', label: 'is not', multi: false },
  { value: 'in', label: 'is any of', multi: true },
]

// Legacy multi-value negation (nin with several chips) — listed only while a
// row is in that state.
export const SEGMENT_LEGACY_SELECT_OPS: SegmentSelectOpDef[] = [
  { value: 'nin', label: 'is none of', multi: true },
]

// Event count qualifiers ("at least / at most / exactly N times"). "Has not
// done" is `eq 0`.
export const SEGMENT_EVENT_OPS: { value: SegmentRuleOp; label: string }[] = [
  { value: 'gte', label: 'at least' },
  { value: 'lte', label: 'at most' },
  { value: 'eq', label: 'exactly' },
]

export const SEGMENT_LEGACY_EVENT_OPS: {
  value: SegmentRuleOp
  label: string
}[] = [
  { value: 'gt', label: 'more than' },
  { value: 'lt', label: 'fewer than' },
  { value: 'neq', label: 'not exactly' },
  { value: 'between', label: 'between' },
]

// ── Segment ──────────────────────────────────────────────────────────────────

export const ICON_KEYS = [
  'users',
  'star',
  'warn',
  'sparkle',
  'crown',
  'cake',
  'trend',
  'shop',
  'sleep',
] as const

export type SegmentIconKey = (typeof ICON_KEYS)[number]

export interface LoyaltySegment {
  id: string
  name: string
  description: string
  // Hex string (e.g. '#F59E0B'); may be '' on legacy docs.
  color: string
  // One of ICON_KEYS; may be '' on legacy docs, render a fallback then.
  icon_key: string
  kind: string
  criteria: SegmentCriteria
  member_count: number
  // Per-member aggregates over the segment's current members, computed by the
  // server alongside member_count. All default to 0 when the server predates
  // them (see gateway normalisation) so the list page renders against either.
  // Mean lifetime spend per member.
  avg_ltv: number
  // Mean order value across members' orders.
  aov: number
  // Mean order count per member.
  avg_orders: number
  // Campaigns targeting this segment with status scheduled or sending.
  active_campaigns: number
  last_evaluated_at: string | null
  // Set only while the segment's LAST EVALUATION FAILED (invalid stored
  // criteria). When present, `member_count` is the last good value rather
  // than a current one, and `last_evaluated_at` is not proof of freshness —
  // a campaign targeting this segment may reach a different set. Null/absent
  // means the figures are live.
  evaluation_error?: string | null
  created_at: string | null
  updated_at: string | null
}

// List-page stats strip aggregate (`GET …/segments` → `summary`). Older
// servers omit it — the gateway derives it client-side from `segments` (see
// `segmentsSummaryFallback` in `features/Loyalty/segments/segmentListDisplay.ts`)
// so it's always populated by the time a caller reads `LoyaltySegmentsList`.
export interface SegmentsSummary {
  total_segments: number
  // Distinct customers across all segments (same number as the list's
  // top-level `customers_covered`).
  customers_covered: number
  // Mean member_count across segments, 1 decimal place.
  avg_segment_size: number
  // member_count-weighted mean of avg_ltv across segments, 2 decimal places.
  weighted_avg_ltv: number
  // member_count of the preset segment named "VIP"; 0 if that preset doesn't
  // exist on this store.
  vip_customers: number
}

// `GET …/segments` list payload. `customers_covered` is the DISTINCT customer
// count across all segments — not a sum of member_count, since one customer
// can sit in several segments. `summary` backs the stats strip; optional on
// the wire (older servers omit it) but the gateway always fills it in.
export interface LoyaltySegmentsList {
  segments: LoyaltySegment[]
  customers_covered: number
  summary?: SegmentsSummary
}

export interface CreateSegmentInput {
  name: string
  description?: string
  color: string
  icon_key: string
  criteria: SegmentCriteria
}

// PUT accepts any subset of the create fields.
export type UpdateSegmentInput = Partial<CreateSegmentInput>

// ── Preview / members ────────────────────────────────────────────────────────

export interface SegmentCustomerSample {
  customer_id: number | string | null
  email: string
  first_name: string
  last_name: string
  loyalty_tier: string
  points_balance: number
  lifetime_points: number
  // Next configured tier above lifetime_points; all three null at the top
  // tier (the server never sends infinity).
  next_tier: string | null
  next_tier_pts: number | null
  pts_to_next_tier: number | null
}

export interface SegmentTierBreakdown {
  none: number
  silver: number
  gold: number
  platinum: number
}

export interface SegmentPreview {
  count: number
  sample: SegmentCustomerSample[]
  tier_breakdown: SegmentTierBreakdown
  total_members: number
  // Per-member aggregates over the preview's matched customers — same shape
  // as the persisted segment's avg_ltv/aov/avg_orders. Older servers omit
  // these; the gateway zero-fills them like `total_members`.
  avg_ltv: number
  aov: number
  avg_orders: number
}

// ── Segment members (detail page table) ─────────────────────────────────────

export interface SegmentMemberLocation {
  city: string
  province: string
}

export interface SegmentMemberOrders {
  total: number
  online: number
  instore: number
}

// Another segment this member also belongs to — the viewed segment itself is
// excluded by the server.
export interface SegmentMemberOtherSegment {
  id: string
  name: string
  color: string
}

export interface SegmentMember {
  customer_id: number | string
  email: string
  first_name: string
  last_name: string
  location: SegmentMemberLocation
  orders: SegmentMemberOrders
  lifetime_value: number
  avg_order_value: number
  last_order_at: string | null
  last_seen_at: string | null
  points_balance: number
  segments: SegmentMemberOtherSegment[]
}

// `sort` on `GET …/segments/{id}/members`. Server defaults to 'ltv' (desc)
// when omitted.
export type SegmentMembersSort = 'ltv' | 'orders' | 'recent' | 'name'

export interface SegmentMembersResponse {
  members: SegmentMember[]
  total: number
  has_more: boolean
  // Next offset to pass back as `cursor`; null once there's no next page.
  cursor: number | null
  sort: SegmentMembersSort
}
